import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import AuroraTargetTemperatureInput from './AuroraTargetTemperatureInput'
import { formatTemperature } from '../utils/string'
import AuroraControl from './AuroraControl'
import { createWarningNotification } from '../redux/slices/notifications'
import { AURORA_MAX_TEMPERATURE_LIMIT, AURORA_MIN_TEMPERATURE_LIMIT } from '../constants/aurora'

interface AuroraTemperatureControlProps {
  value: number | null
  min?: number | null
  max?: number | null
  loading?: boolean
  disabled?: boolean
  onSubmit: (value: number) => Promise<void>
}

export default function AuroraTargetTemperatureControl({
  value: targetTemperature,
  loading = false,
  disabled = false,
  min = null,
  max = null,
  onSubmit
}: AuroraTemperatureControlProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  /**
   * The new target temperature.
   */
  const [newTargetTemperature, setNewTargetTemperature] = useState<number | null>(null)

  const clampCeil = (value: number) => {
    const clamped = Math.min(value, max ?? AURORA_MAX_TEMPERATURE_LIMIT)

    return Math.max(clamped, min ?? AURORA_MIN_TEMPERATURE_LIMIT)
  }

  const clampFloor = (value: number) => {
    const clamped = Math.max(value, min ?? AURORA_MIN_TEMPERATURE_LIMIT)

    return Math.min(clamped, max ?? AURORA_MAX_TEMPERATURE_LIMIT)
  }

  /**
   * Increments the target temperature by 0.5 degrees.
   */
  function handleIncrement() {
    const value = newTargetTemperature !== null ? newTargetTemperature : targetTemperature

    if (value === null) {
      return
    }

    const nextValue = Math.ceil(value * 2) / 2 + 0.5
    const maxLimit = max ?? AURORA_MAX_TEMPERATURE_LIMIT

    if (nextValue > maxLimit) {
      dispatch(createWarningNotification(t('maxTemperatureLimitWarning', { value: maxLimit })))
    }

    setNewTargetTemperature(clampCeil(nextValue))
  }

  /**
   * Decrements the target temperature by 0.5 degrees.
   */
  function handleDecrement() {
    const value = newTargetTemperature !== null ? newTargetTemperature : targetTemperature

    if (value === null) {
      return
    }

    const nextValue = Math.floor(value * 2) / 2 - 0.5
    const minLimit = min ?? AURORA_MIN_TEMPERATURE_LIMIT

    if (nextValue < minLimit) {
      dispatch(createWarningNotification(t('minTemperatureLimitWarning', { value: minLimit })))
    }

    setNewTargetTemperature(clampFloor(nextValue))
  }

  /**
   * Update the target temperature.
   */
  async function handleConfirm() {
    if (newTargetTemperature === null) {
      return
    }

    try {
      await onSubmit(newTargetTemperature)

      setNewTargetTemperature(null)
    } catch {
      // Ignore the error.
    }
  }

  /**
   * Reset the new target temperature.
   */
  function handleReset() {
    setNewTargetTemperature(null)
  }

  return (
    <AuroraControl
      direction="column"
      label={t('targetTemperature')}
      loading={loading}
      disabled={disabled}
      control={
        <Stack direction="column" justifyContent="center" alignItems="center" gap={2} pt={2}>
          <Stack direction="row" alignItems="center" gap={3}>
            {targetTemperature !== null && (
              <IconButton
                color={newTargetTemperature !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{ border: '1px solid' }}
                onClick={handleDecrement}
              >
                <Remove />
              </IconButton>
            )}

            <Stack direction="column" justifyContent="center" alignItems="center" gap={1}>
              {newTargetTemperature !== null ? (
                <AuroraTargetTemperatureInput
                  value={newTargetTemperature}
                  loading={loading}
                  onChange={setNewTargetTemperature}
                  onSubmit={handleConfirm}
                />
              ) : targetTemperature !== null ? (
                <Typography variant="h2" color="text.primary">
                  {formatTemperature(targetTemperature)}
                </Typography>
              ) : (
                <Skeleton variant="text" width={80} height={48} />
              )}
            </Stack>

            {targetTemperature !== null && (
              <IconButton
                color={newTargetTemperature !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{
                  border: '2px solid'
                }}
                onClick={handleIncrement}
              >
                <Add />
              </IconButton>
            )}
          </Stack>

          {newTargetTemperature !== null && (
            <Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
              <LoadingButton
                variant="outlined"
                color="primary"
                size="small"
                loading={loading}
                disabled={disabled}
                onClick={handleConfirm}
              >
                {t('actions:confirm')}
              </LoadingButton>

              <Button
                variant="text"
                color="inherit"
                size="small"
                disabled={loading || disabled}
                onClick={handleReset}
              >
                {t('actions:cancel')}
              </Button>
            </Stack>
          )}
        </Stack>
      }
    />
  )
}
